/**
 * Artifact Constructor - Platform Governance layer.
 *
 * Single pure function that assembles a fully formed ArtifactEnvelope with
 * engine-produced content at orchestration boundaries.
 *
 * Contract (frozen):
 *   - Input: envelope (ArtifactEnvelope - fully formed by caller), content (C)
 *   - Output: ArtifactEnvelope & C (the complete artifact value object)
 *   - No side effects. No I/O. No version assignment. No timestamp generation.
 *     No envelope construction from primitives.
 *
 * Responsibility boundary:
 *   constructArtifact  - pure spread assembly only; accepts a complete envelope
 *   persistence layer  - assigns version (MAX+1), assigns createdAt via database
 *                        DEFAULT now() + RETURNING, enforces UNIQUE constraint,
 *                        retries on conflict, writes to database
 *
 * Usage:
 *   import { constructArtifact } from "@/platform/artifactConstructor";
 *   // envelope comes from persistence layer after reading RETURNING values:
 *   const envelope: ArtifactEnvelope = { projectId, artifactType, version, createdAt };
 *   const artifact = constructArtifact(envelope, blueprintContent);
 *
 * Model B compliance:
 *   Engines return content only. This function is the single composition point
 *   where content becomes a governed artifact. It never lives inside an engine.
 */

import type { ArtifactEnvelope } from "@/types/platformArtifactTypes";

/**
 * Assemble a governed artifact value from a fully formed envelope and
 * engine-produced content. Pure spread - no construction logic.
 */
export function constructArtifact<C extends object>(
  envelope: ArtifactEnvelope,
  content:  C,
): ArtifactEnvelope & C {
  return { ...envelope, ...content } as ArtifactEnvelope & C;
}
